// Which sidebar pages a specialist role is shown — the "lock down features by role" rule.
//
// A member whose role writes one corner of the platform (a Data Protection Officer, an HR
// lead, a TPRM manager, an incident manager) sees the pages of that corner, plus the pages
// everyone uses (Home, Documents, Reports, Settings, Help). The registers they can neither
// change nor are responsible for are left out of their sidebar so it reads as their job.
//
// This is navigation, never access control: reading stays open to every member and the
// server enforces every write (backend/src/middleware/permissions.ts). A hidden page is still
// reachable by its URL and by any link to it.
//
// Who is NOT narrowed:
//   - anyone holding controls:write or org:admin (they run the management system, and an
//     administrator sees everything — backend isClientAdmin);
//   - roles with no write permission at all (viewer, external reviewer): reading is their job,
//     so they keep the whole map;
//   - read-oversight roles whose writes are about everything (internal auditor, executive
//     sponsor): audit:write and reports:generate on their own leave the map whole.

import type { NavSection, NavItem } from "./types";

/** The permissions that make a page "theirs". Keyed by nav item or tab id (clientNav.ts). */
const PAGE_OWNERS: Readonly<Record<string, readonly string[]>> = {
  // My standards — the whole section.
  "section:standards": ["controls:write", "compliance:run"],
  // Laws & privacy.
  legal: ["legal:write", "privacy:write"],
  "gdpr-ropa": ["privacy:write"],
  "gdpr-requests": ["privacy:write"],
  "gdpr-dpia": ["privacy:write"],
  "gdpr-breaches": ["privacy:write"],
  "data-protection-mapping": ["privacy:write", "legal:write"],
  "data-discovery": ["privacy:write"],
  submissions: ["submissions:prepare", "submissions:send"],
  // Risk & suppliers.
  "risk-register": ["risks:write"],
  tprm: ["tprm:write"],
  "threat-intelligence": ["risks:write", "resilience:write"],
  vulnerabilities: ["resilience:write", "remediation:run"],
  exposure: ["risks:write"],
  // Resilience.
  resilience: ["resilience:write"],
  // "resilience-incidents" is deliberately absent: any member may report an incident
  // (POST /resilience/incidents/report), and the Incidents page is where they do it.
  "resilience-bia": ["resilience:write"],
  "resilience-assets": ["resilience:write"],
  "resilience-exercises": ["resilience:write"],
  "resilience-training": ["resilience:write", "people:write"],
  "resilience-insurance": ["resilience:write"],
  dora: ["resilience:write"],
  // Improvement — every register owner may raise a corrective action on what they own
  // (backend improvement.routes.ts ACTION_WRITE_PERMISSIONS).
  improvement: ["risks:write", "tprm:write", "resilience:write", "legal:write", "privacy:write", "audit:write"],
  remediation: ["remediation:run"],
  // Security & AI.
  estate: ["resilience:write", "integrations:admin"],
  hardening: ["remediation:run"],
  "ai-systems": ["risks:write"],
};

/** Permissions that make a role broad: the whole map stays. */
const BROAD = ["controls:write", "org:admin"];
/** Write permissions that are about the whole organisation, so they do not narrow anyone on their own. */
const OVERSIGHT = new Set(["audit:write", "reports:generate", "billing:view"]);

/** Whether this permission set belongs to a specialist whose sidebar is narrowed to their job. */
export function isSpecialistRole(permissions: readonly string[]): boolean {
  if (BROAD.some((p) => permissions.includes(p))) return false;
  return permissions.some((p) => !OVERSIGHT.has(p) && !p.startsWith("group:"));
}

const holdsAny = (permissions: readonly string[], owners: readonly string[] | undefined) =>
  !owners || owners.some((p) => permissions.includes(p));

/** The sections with a specialist's out-of-job pages hidden. Pure; returns the input untouched for everyone else. */
export function applyRoleFocus(sections: NavSection[], permissions: readonly string[]): NavSection[] {
  if (!isSpecialistRole(permissions)) return sections;
  const focusItem = (item: NavItem): NavItem => {
    if (item.hidden) return item;
    if (!item.tabs) return holdsAny(permissions, PAGE_OWNERS[item.id]) ? item : { ...item, hidden: true };
    const tabs = item.tabs.map((t) => (t.hidden || holdsAny(permissions, PAGE_OWNERS[t.id]) ? t : { ...t, hidden: true }));
    return { ...item, tabs, hidden: tabs.every((t) => t.hidden) };
  };
  return sections
    .map((section) => {
      if (!holdsAny(permissions, PAGE_OWNERS[`section:${section.id}`])) {
        return { ...section, items: section.items.map((i) => ({ ...i, hidden: true })) };
      }
      return { ...section, items: section.items.map(focusItem) };
    })
    .filter((section) => section.items.some((item) => !item.hidden));
}
